import firebase from 'firebase/app';
import { getConfig } from '../../../../config/config';
import {
  ANALYTICS_ENDPOINT,
  POSTHOG_ONLY_EVENTS,
  DUAL_SEND_EVENTS,
  SENSITIVE_PROPERTY_FIELDS,
  PII_NETWORK_FIELDS,
  MAX_RETRIES,
  RETRY_DELAY_MS,
  QUEUE_KEY,
  MAX_QUEUE_SIZE,
} from './constants';
import { getEnrichmentData } from './enrichment';

const API_ENDPOINT = getConfig('REACT_APP_CLOUD_API_ENDPOINT');

/**
 * Checks if event should go to PostHog only (not backend)
 */
export function isPostHogOnlyEvent(eventName) {
  return POSTHOG_ONLY_EVENTS.includes(eventName);
}

/**
 * Checks if event should go to BOTH backend and PostHog
 */
export function isDualSendEvent(eventName) {
  return DUAL_SEND_EVENTS.includes(eventName);
}

/**
 * Queue failed events to localStorage for retry on next session
 */
function queueForRetry(payload) {
  try {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    queue.push({ payload, timestamp: Date.now() });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch (e) {
    // localStorage might be full or disabled
  }
}

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * Send event to backend with retry logic.
 * Uses fetch directly (not apiFetch) to handle 204 empty-body
 * responses without failing on res.json().
 */
async function sendToBackend(payload, retryCount = 0) {
  try {
    const token = (
      await firebase.auth().currentUser?.getIdToken()
    ) || '';
    const res = await fetch(
      `${API_ENDPOINT}${ANALYTICS_ENDPOINT}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      await delay(RETRY_DELAY_MS * (2 ** retryCount));
      await sendToBackend(payload, retryCount + 1);
      return;
    }
    queueForRetry(payload);
  }
}

/**
 * Process queued events from previous sessions.
 * Clears only after all sends settle so a tab crash
 * mid-flight won't lose queued events.
 */
export async function processRetryQueue() {
  try {
    const queue = JSON.parse(
      localStorage.getItem(QUEUE_KEY) || '[]',
    );
    if (queue.length === 0) return;

    const processedCount = queue.length;

    await Promise.allSettled(
      queue.map(({ payload }) => sendToBackend(payload)),
    );

    // Preserve items queued during processing (re-queued failures
    // or new events); only remove the batch we just processed.
    const current = JSON.parse(
      localStorage.getItem(QUEUE_KEY) || '[]',
    );
    const remaining = current.slice(processedCount);
    if (remaining.length > 0) {
      localStorage.setItem(
        QUEUE_KEY,
        JSON.stringify(remaining),
      );
    } else {
      localStorage.removeItem(QUEUE_KEY);
    }
  } catch (e) {
    // Ignore queue processing errors
  }
}

/**
 * Strips sensitive fields from an object (shallow).
 */
export function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return properties;

  const sanitized = { ...properties };
  SENSITIVE_PROPERTY_FIELDS.forEach((field) => {
    if (sanitized[field] !== undefined) {
      sanitized[field] = '**redacted**';
    }
  });
  return sanitized;
}

/**
 * Recursively strips PII fields from nested objects/arrays.
 * Used for LogRocket network body sanitization where API responses
 * have nested structures like { data: { email, firstName, ... } }.
 */
export function sanitizeNetworkBody(obj) {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeNetworkBody(item));
  }

  const sanitized = {};
  Object.keys(obj).forEach((key) => {
    if (PII_NETWORK_FIELDS.has(key) && obj[key] !== null && obj[key] !== undefined) {
      sanitized[key] = '**redacted**';
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      sanitized[key] = sanitizeNetworkBody(obj[key]);
    } else {
      sanitized[key] = obj[key];
    }
  });
  return sanitized;
}

/**
 * Sanitize a PostHog event before it reaches PostHog servers.
 * Strips sensitive values from properties and autocapture element data.
 */
function sanitizePostHogEvent(event) {
  if (!event) return event;

  const sanitized = {
    ...event,
    properties: sanitizeProperties(event.properties),
    ...(event.$set && { $set: sanitizeProperties(event.$set) }),
    ...(event.$set_once && { $set_once: sanitizeProperties(event.$set_once) }),
  };

  // Autocapture events may carry input values in $elements
  if (sanitized.properties?.$elements) {
    sanitized.properties.$elements = sanitized.properties.$elements.map((el) => {
      const cleanEl = { ...el };
      if (cleanEl.value !== undefined) {
        cleanEl.value = '**redacted**';
      }
      return cleanEl;
    });
  }

  return sanitized;
}

/**
 * Strips PostHog internal properties from event data before sending to backend.
 * PostHog adds $-prefixed metadata ($os, $browser, $device_type, $lib, etc.)
 * and meta fields (token, distinct_id) to every event. These are not needed
 * in the backend payload — useful metadata is already captured in context.
 */
const POSTHOG_META_FIELDS = new Set(['token', 'distinct_id']);

function stripPostHogProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return properties;

  const cleaned = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (!key.startsWith('$') && !POSTHOG_META_FIELDS.has(key)) {
      cleaned[key] = value;
    }
  });
  return cleaned;
}

/**
 * Builds the payload for backend
 * API spec: eventType, source (required), userId, data, context (optional)
 */
function buildBackendPayload(event) {
  const enrichment = getEnrichmentData();

  return {
    eventType: event.event,
    source: enrichment.source,
    userId: event.properties?.distinct_id || event.distinct_id || null,
    data: {
      ...sanitizeProperties(stripPostHogProperties(event.properties)),
      platform: enrichment.platform,
    },
    context: {
      page: window.location.href,
      referrer: enrichment.referrer,
      userAgent: enrichment.userAgent,
      sessions: {
        posthog: {
          sessionId: event.properties?.$session_id || null,
          distinctId: event.properties?.distinct_id || event.distinct_id || null,
        },
      },
      utm: {
        utm_source: enrichment.utm_source || null,
        utm_medium: enrichment.utm_medium || null,
        utm_campaign: enrichment.utm_campaign || null,
        utm_term: enrichment.utm_term || null,
        utm_content: enrichment.utm_content || null,
        gclid: enrichment.gclid || null,
        fbclid: enrichment.fbclid || null,
        msclkid: enrichment.msclkid || null,
        ttclid: enrichment.ttclid || null,
        gbraid: enrichment.gbraid || null,
        wbraid: enrichment.wbraid || null,
      },
    },
  };
}

/**
 * Creates the before_send hook for PostHog config.
 * - PostHog-only events ($pageview, $autocapture, etc.) → PostHog only
 * - Dual-send events → backend + PostHog
 * - Custom events → backend only (blocked from PostHog)
 */
export function createBeforeSendHook() {
  return (event) => {
    if (!event) return null;

    const eventName = event.event;

    // PostHog-only events: don't send to backend, let PostHog handle
    if (isPostHogOnlyEvent(eventName)) {
      return sanitizePostHogEvent(event);
    }

    // Dual-send events: send to backend AND PostHog
    if (isDualSendEvent(eventName)) {
      sendToBackend(buildBackendPayload(event));
      return sanitizePostHogEvent(event);
    }

    // Custom events: send to backend only, block from PostHog
    sendToBackend(buildBackendPayload(event));
    return null;
  };
}

// Legacy class kept for backwards compatibility if needed
class AnalyticsMiddleware {
  constructor(posthog) {
    this.posthog = posthog;
    this.initialized = false;
  }

  init() {
    if (this.initialized || !this.posthog) return;
    processRetryQueue();
    this.initialized = true;
  }
}

export default AnalyticsMiddleware;
